import { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Loading from '../ui/Loading';
import { RootState } from '../../store/indexRedux';
import { useTranslation } from 'react-i18next';

interface DocumentViewerProps {
  fetchDocument: (kboNumber: string, documentId: string) => Promise<string>;
  backLink: string;
  title: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ fetchDocument, backLink, title }) => {
  const { t } = useTranslation('DocumentsTable');
  const { documentId } = useParams<{ documentId: string }>();
  const { selectedUserCompany } = useSelector((state: RootState) => state.selectedCompany);
  const [document, setDocument] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedUserCompany?.companyNumber && documentId) {
        try {
          const documentData = await fetchDocument(selectedUserCompany.companyNumber, documentId as string);
          setDocument(documentData);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
          setError((err as Error).message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [selectedUserCompany?.companyNumber, documentId, fetchDocument]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (loading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  const isImage = document?.endsWith('.png') || document?.endsWith('.jpg') || document?.endsWith('.jpeg');
  const isCryptoSigned = documentId?.includes('signedencrypted') ?? false;

  return (
    <div className='md:mx-16  pb-[80px] mt-20 mb-16'>
      <h2 className='text-primary-600 dark:text-white lg:text-[1.875rem] font-semibold mb-16'>{title}</h2>
      <p className='text-primary-400 text-sm font-semibold'>{documentId}</p>
      <div className='mt-8 mb-8'>
        <NavLink
          to={backLink}
          className='px-[1.25rem] py-[0.53rem] bg-primary-600 dark:bg-primary-600 text-sm text-white rounded-3xl hover:bg-primary-700 dark:hover:bg-primary-700 font-semibold '
        >
          {t('goBack')}
        </NavLink>
      </div>

      {isCryptoSigned && (
        <div className='flex items-center gap-2.5 px-4 py-3 mb-6 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'>
          <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5 shrink-0 text-green-600 dark:text-green-400' viewBox='0 0 24 24' fill='currentColor'>
            <path fillRule='evenodd' d='M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3A5.25 5.25 0 0012 1.5zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z' clipRule='evenodd' />
          </svg>
          <span className='text-sm font-medium text-green-800 dark:text-green-300'>
            Digitally Signed — PKCS#7 cryptographic signature embedded. Open in Adobe Reader to verify authenticity.
          </span>
        </div>
      )}

      {isImage ? (
        <img src={document || ''} alt={title} className='w-full h-auto' />
      ) : (
        <iframe src={document || ''} width='100%' height='700px' title={title}></iframe>
      )}
    </div>
  );
};

export default DocumentViewer;
